import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Grid, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, TextField, FormControl,
  InputLabel, MenuItem, Select, Card, CardContent
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { authToken } from '../globals/Global';
import { getCatManData, addPujaCategory, addPujaSubCategory, updatePujaCategory, deletePujaCategory, updatePujaSubCategory, deletePujaSubCategory} from '../globals/Services';

export default function PoojaCategoryDashboard() {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState({ name: '', imageUrl: '' });
  const [subCategory, setSubCategory] = useState({
    poojaCategoryId: '',
    name: '',
    description: '',
    price: '',
    offerPrice: '',
    imageUrl: '',
    durationTime: '',
    remarks: ''
  });

  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // Dialog open states
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openSubCategoryDialog, setOpenSubCategoryDialog] = useState(false);
  const [openEditSubCategoryDialog, setOpenEditSubCategoryDialog] = useState(false);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [openEditCategoryDialog, setOpenEditCategoryDialog] = useState(false);
  const [openDeleteCategoryConfirmDialog, setOpenDeleteCategoryConfirmDialog] = useState(false);
  
  // Edit state
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [deletingSubCategory, setDeletingSubCategory] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);

  useEffect(() => {
  const fetchCategories = async () => {
    try {
      const token = authToken || localStorage.getItem('sessionToken');

      const res = await fetch(getCatManData, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const result = await res.json();
      const categoryData = result?.data || [];

      // Sort categories by 'id' or any other field you want
      const sortedCategories = categoryData
        .map(cat => ({
          id: cat.mainPoojaCategoryId,
          name: cat.name,
          imageUrl: cat.imageUrl || '',
          poojaSubcategories: cat.poojaSubcategories || []
        }))
        .sort((a, b) => a.id - b.id);  // Sorting by 'id' in ascending order

      setCategories(sortedCategories);
      setSelectedCategoryId(sortedCategories[0]?.id || null);

      // Map subcategories with imageUrl as first image in images array
      const allSubs = sortedCategories.flatMap(cat =>
        cat.poojaSubcategories.map(sub => ({
          id: sub.poojaSubcategoryId,
          poojaCategoryId: cat.id,
          name: sub.name,
          description: sub.description,
          price: sub.price,
          offerPrice: sub.offerPrice,
          remarks: sub.remarks,
          durationTime: sub.durationTime,
          imageUrl: sub.images && sub.images.length > 0 ? sub.images[0] : '',
        }))
      );

      setSubCategories(allSubs);

      // Set default poojaCategoryId in subCategory form if empty
      if (sortedCategories.length > 0 && !subCategory.poojaCategoryId) {
        setSubCategory(prev => ({
          ...prev,
          poojaCategoryId: sortedCategories[0].id
        }));
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Error fetching categories');
    }
  };

  fetchCategories();
}, [subCategory.poojaCategoryId]);


  const handleCategorySubmit = async () => {
    const token = authToken || localStorage.getItem('sessionToken');

    try {
      const res = await fetch(addPujaCategory, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: category.name,
          imageUrl: category.imageUrl
        })
      });

      const result = await res.json();

      if (res.ok) {
        const newCat = {
          id: result.data?.poojaCategoryId || result.id || Date.now().toString(),
          name: category.name,
          imageUrl: category.imageUrl,
          poojaSubcategories: []
        };
        setCategories(prev => [...prev, newCat]);
        setSelectedCategoryId(newCat.id);
        setCategory({ name: '', imageUrl: '' });
        setOpenCategoryDialog(false);
        toast.success('Category added successfully!');
      } else {
        console.error('Failed to create category:', result);
        toast.error('Error: ' + (result?.message || 'Could not add category.'));
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error('An error occurred while adding the category.');
    }
  };

  const handleSubCategorySubmit = async () => {
    const token = authToken || localStorage.getItem('sessionToken');

    try {
      const res = await fetch(addPujaSubCategory, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          poojaCategoryId: Number(subCategory.poojaCategoryId),
          name: subCategory.name,
          description: subCategory.description,
          images: [subCategory.imageUrl],
          price: Number(subCategory.price),
          offerPrice: Number(subCategory.offerPrice),
          remarks: subCategory.remarks,
          durationTime: subCategory.durationTime
        })
      });

      const result = await res.json();

      if (res.ok) {
        const newSub = {
          ...subCategory,
          id: result.data?.poojaSubcategoryId || result.id || Date.now().toString(),
          poojaCategoryId: subCategory.poojaCategoryId
        };
        setSubCategories(prev => [...prev, newSub]);
        setSubCategory({
          poojaCategoryId: categories[0]?.id || '',
          name: '',
          description: '',
          price: '',
          offerPrice: '',
          imageUrl: '',
          durationTime: '',
          remarks: ''
        });
        setOpenSubCategoryDialog(false);
        toast.success('Subcategory added successfully!');
      } else {
        console.error('Failed to add subcategory:', result);
        toast.error('Error: ' + (result?.message || 'Could not add subcategory.'));
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error('An error occurred while adding the subcategory.');
    }
  };

  // Handle edit category
  const handleEditCategory = (cat) => {
    setEditingCategory({
      id: cat.id,
      name: cat.name,
      imageUrl: cat.imageUrl
    });
    setOpenEditCategoryDialog(true);
  };

  // Handle update category submit
  const handleUpdateCategorySubmit = async () => {
    const token = authToken || localStorage.getItem('sessionToken');

    try {
      const res = await fetch(updatePujaCategory, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          id: Number(editingCategory.id),
          name: editingCategory.name,
          imageUrl: editingCategory.imageUrl
        })
      });

      const result = await res.json();

      if (res.ok) {
        // Update the category in the local state
        setCategories(prev => prev.map(cat => 
          cat.id === editingCategory.id 
            ? { ...cat, name: editingCategory.name, imageUrl: editingCategory.imageUrl }
            : cat
        ));
        
        setEditingCategory(null);
        setOpenEditCategoryDialog(false);
        toast.success('Category updated successfully!');
      } else {
        console.error('Failed to update category:', result);
        toast.error('Error: ' + (result?.message || 'Could not update category.'));
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error('An error occurred while updating the category.');
    }
  };

  // Handle delete category
  const handleDeleteCategory = (cat) => {
    setDeletingCategory(cat);
    setOpenDeleteCategoryConfirmDialog(true);
  };

  // Handle confirm delete category
  const handleConfirmDeleteCategory = async () => {
    const token = authToken || localStorage.getItem('sessionToken');

    try {
      const res = await fetch(deletePujaCategory(deletingCategory.id), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (res.ok) {
        // Remove the category from local state
        setCategories(prev => prev.filter(cat => cat.id !== deletingCategory.id));
        
        // Remove associated subcategories
        setSubCategories(prev => prev.filter(sub => String(sub.poojaCategoryId) !== String(deletingCategory.id)));
        
        // Reset selected category if it was deleted
        if (String(selectedCategoryId) === String(deletingCategory.id)) {
          const remainingCategories = categories.filter(cat => cat.id !== deletingCategory.id);
          setSelectedCategoryId(remainingCategories[0]?.id || null);
        }
        
        setDeletingCategory(null);
        setOpenDeleteCategoryConfirmDialog(false);
        toast.success('Category deleted successfully!');
      } else {
        const result = await res.json();
        console.error('Failed to delete category:', result);
        toast.error('Error: ' + (result?.message || 'Could not delete category.'));
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error('An error occurred while deleting the category.');
    }
  };

  // Handle edit subcategory
  const handleEditSubCategory = (subCat) => {
    setEditingSubCategory({
      id: subCat.id,
      poojaCategoryId: subCat.poojaCategoryId,
      name: subCat.name,
      description: subCat.description,
      price: subCat.price,
      offerPrice: subCat.offerPrice,
      imageUrl: subCat.imageUrl,
      durationTime: subCat.durationTime,
      remarks: subCat.remarks
    });
    setOpenEditSubCategoryDialog(true);
  };

  // Handle update subcategory submit
  const handleUpdateSubCategorySubmit = async () => {
    const token = authToken || localStorage.getItem('sessionToken');

    try {
      const res = await fetch(updatePujaSubCategory, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          id: Number(editingSubCategory.id),
          poojaCategoryId: Number(editingSubCategory.poojaCategoryId),
          name: editingSubCategory.name,
          description: editingSubCategory.description,
          images: [editingSubCategory.imageUrl],
          price: Number(editingSubCategory.price),
          offerPrice: Number(editingSubCategory.offerPrice),
          remarks: editingSubCategory.remarks,
          durationTime: editingSubCategory.durationTime
        })
      });

      const result = await res.json();

      if (res.ok) {
        // Update the subcategory in the local state
        setSubCategories(prev => prev.map(sub => 
          sub.id === editingSubCategory.id 
            ? { ...editingSubCategory }
            : sub
        ));
        
        setEditingSubCategory(null);
        setOpenEditSubCategoryDialog(false);
        toast.success('Subcategory updated successfully!');
      } else {
        console.error('Failed to update subcategory:', result);
        toast.error('Error: ' + (result?.message || 'Could not update subcategory.'));
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error('An error occurred while updating the subcategory.');
    }
  };

  // Handle delete subcategory
  const handleDeleteSubCategory = (subCat) => {
    setDeletingSubCategory(subCat);
    setOpenDeleteConfirmDialog(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    const token = authToken || localStorage.getItem('sessionToken');

    try {
      const res = await fetch(deletePujaSubCategory(deletingSubCategory.id), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (res.ok) {
        // Remove the subcategory from local state
        setSubCategories(prev => prev.filter(sub => sub.id !== deletingSubCategory.id));
        setDeletingSubCategory(null);
        setOpenDeleteConfirmDialog(false);
        toast.success('Subcategory deleted successfully!');
      } else {
        const result = await res.json();
        console.error('Failed to delete subcategory:', result);
        toast.error('Error: ' + (result?.message || 'Could not delete subcategory.'));
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error('An error occurred while deleting the subcategory.');
    }
  };

  const filteredSubCategories = selectedCategoryId
    ? subCategories.filter(sub => String(sub.poojaCategoryId) === String(selectedCategoryId))
    : subCategories;

  return (
    <Box sx={{ height: '100vh', overflow: 'hidden', p: 2 }}>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
  {/* Header with Buttons */}
  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
    <Typography variant="h5" fontWeight="600">
      Category Management
    </Typography>
    
    <Box display="flex" gap={2}>
      {/* Upload CSV Button */}
      <input
        accept=".csv"
        style={{ display: 'none' }}
        id="csv-upload"
        type="file"
        onChange={async (e) => {
          const file = e.target.files[0];
          if (!file) return;
          const token = authToken || localStorage.getItem('sessionToken');
          const formData = new FormData();
          formData.append('file', file);

          try {
            const res = await fetch('http://stg.panditjee.com:8080/api/category/import', {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`
              },
              body: formData
            });
            const result = await res.json();
            if (res.ok) {
              toast.success('CSV uploaded successfully!');
            } else {
              toast.error(result?.message || 'Failed to upload CSV');
            }
          } catch (error) {
            toast.error('Error uploading CSV');
          }
          e.target.value = ''; // Reset input
        }}
      />
      <label htmlFor="csv-upload">
        <Button
          variant="contained"
          color="info"
          size="large"
          component="span"
          sx={{
            fontWeight: 'bold',
            textTransform: 'none'
          }}
        >
          ⬆️ Upload Cat
        </Button>
      </label>
      {/* Existing Add Category Button */}
      <Button 
        variant="contained" 
        color="primary"
        size="large"
        onClick={() => setOpenCategoryDialog(true)}
        sx={{ 
          fontWeight: 'bold',
          textTransform: 'none'
        }}
      >
        ➕ Add Category
      </Button>
      <Button 
        variant="contained" 
        color="secondary"
        size="large"
        onClick={() => setOpenSubCategoryDialog(true)}
        sx={{ 
          fontWeight: 'bold',
          textTransform: 'none'
        }}
      >
        ➕ Add Subcategory
      </Button>
    </Box>
  </Box>

  {/* Main Content Box (Replacing Grid with Box) */}
  <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} spacing={2} sx={{ height: 'calc(100vh - 120px)' }}>
    {/* Left Column - Categories */}
    <Box flex={1} sx={{ display: 'flex', flexDirection: 'column' }}>
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
            📿 Pooja Categories
          </Typography>
          <Box sx={{ flexGrow: 1, overflow: 'auto', pr: 1, pb: 3, maxHeight: 'calc(100vh - 180px)' }}>
            {categories.length > 0 ? (
              categories.map(cat => (
                <Box
                  key={cat.id}
                  mb={1.5}
                  p={2}
                  border={1}
                  borderRadius={2}
                  borderColor={selectedCategoryId === cat.id ? 'primary.main' : 'grey.300'}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.light',
                      backgroundColor: 'grey.50'
                    },
                    backgroundColor: selectedCategoryId === cat.id ? 'primary.50' : 'white'
                  }}
                  onClick={() => setSelectedCategoryId(cat.id)}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    {/* Category Image */}
                    {cat.imageUrl ? (
                      <img
                        src={cat.imageUrl}
                        alt={cat.name}
                        style={{ 
                          width: '60px', 
                          height: '60px', 
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '2px solid #ddd'
                        }}
                      />
                    ) : (
                      <Box 
                        sx={{ 
                          width: '60px', 
                          height: '60px', 
                          backgroundColor: 'grey.200',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          No Image
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Category Details */}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {cat.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {cat.id}
                      </Typography>
                      <Typography variant="caption" color="primary">
                        {cat.poojaSubcategories?.length || 0} subcategories
                      </Typography>
                    </Box>

                    {/* Action Buttons Column */}
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 1,
                        alignItems: 'center'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton
                        onClick={() => handleEditCategory(cat)}
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'white',
                          width: 32,
                          height: 32,
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          }
                        }}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      
                      <IconButton
                        onClick={() => handleDeleteCategory(cat)}
                        sx={{
                          backgroundColor: 'error.main',
                          color: 'white',
                          width: 32,
                          height: 32,
                          '&:hover': {
                            backgroundColor: 'error.dark',
                          }
                        }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Box textAlign="center" mt={4}>
                <Typography variant="body1" color="text.secondary">
                  No categories available
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>

    {/* Right Column - Subcategories */}
    <Box flex={1} sx={{ display: 'flex', flexDirection: 'column' }}>
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color="secondary" fontWeight="bold">
              🔥 Pooja Subcategories
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1, overflow: 'auto', pr: 1, pb: 3, maxHeight: 'calc(100vh - 180px)' }}>
            {filteredSubCategories.length > 0 ? (
              filteredSubCategories.map(sub => (
                <Box key={sub.id} mb={1.5} p={2} border={1} borderRadius={2} borderColor="grey.300"
                     sx={{ backgroundColor: 'grey.50' }}>
                  <Box display="flex" gap={2} alignItems="center">
                    {/* Subcategory Image */}
                    {sub.imageUrl ? (
                      <img
                        src={sub.imageUrl}
                        alt={sub.name}
                        style={{ 
                          width: '80px', 
                          height: '60px', 
                          objectFit: 'cover',
                          borderRadius: '6px',
                          border: '1px solid #ddd'
                        }}
                      />
                    ) : (
                      <Box 
                        sx={{ 
                          width: '80px', 
                          height: '60px', 
                          backgroundColor: 'grey.200',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          No Image
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Subcategory Details */}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">
                        {sub.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        ID: {sub.id}
                      </Typography>
                      <Box display="flex" gap={2} mt={0.5}>
                        <Typography variant="body2" color="success.main">
                          ₹{sub.price}
                        </Typography>
                        <Typography variant="body2" color="error.main">
                          Offer: ₹{sub.offerPrice}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Duration: {sub.durationTime}
                      </Typography>
                    </Box>

                    {/* Action Buttons Column */}
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 1,
                        alignItems: 'center'
                      }}
                    >
                      <IconButton
                        onClick={() => handleEditSubCategory(sub)}
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'white',
                          width: 32,
                          height: 32,
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          }
                        }}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      
                      <IconButton
                        onClick={() => handleDeleteSubCategory(sub)}
                        sx={{
                          backgroundColor: 'error.main',
                          color: 'white',
                          width: 32,
                          height: 32,
                          '&:hover': {
                            backgroundColor: 'error.dark',
                          }
                        }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <Box textAlign="center" mt={4}>
                <Typography variant="body1" color="text.secondary">
                  {selectedCategoryId ? 'No subcategories for this category' : 'Select a category to view subcategories'}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  </Box>


      {/* Add Category Dialog */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white' }}>
          Add Pooja Category
          <IconButton
            aria-label="close"
            onClick={() => setOpenCategoryDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <TextField
            fullWidth
            label="Category Name"
            margin="normal"
            value={category.name}
            onChange={e => setCategory({ ...category, name: e.target.value })}
            variant="outlined"
          />

          <TextField
            fullWidth
            label="Image URL"
            margin="normal"
            value={category.imageUrl}
            onChange={e => setCategory({ ...category, imageUrl: e.target.value })}
            variant="outlined"
          />

          <Button 
            variant="contained" 
            fullWidth 
            onClick={handleCategorySubmit} 
            sx={{ mt: 3, py: 1.5, fontWeight: 'bold' }}
            size="large"
          >
            Create Category
          </Button>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog
        open={openEditCategoryDialog}
        onClose={() => setOpenEditCategoryDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: 'warning.main', color: 'white' }}>
          Edit Pooja Category
          <IconButton
            aria-label="close"
            onClick={() => setOpenEditCategoryDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {editingCategory && (
            <>
              <TextField
                fullWidth
                label="Category Name"
                margin="normal"
                value={editingCategory.name}
                onChange={e => setEditingCategory({ ...editingCategory, name: e.target.value })}
                variant="outlined"
              />

              <TextField
                fullWidth
                label="Image URL"
                margin="normal"
                value={editingCategory.imageUrl}
                onChange={e => setEditingCategory({ ...editingCategory, imageUrl: e.target.value })}
                variant="outlined"
              />

              <Button 
                variant="contained" 
                fullWidth 
                onClick={handleUpdateCategorySubmit} 
                sx={{ mt: 3, py: 1.5, fontWeight: 'bold', backgroundColor: 'warning.main' }}
                size="large"
              >
                Update Category
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation Dialog */}
      <Dialog
        open={openDeleteCategoryConfirmDialog}
        onClose={() => setOpenDeleteCategoryConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: 'error.main', color: 'white' }}>
          Confirm Delete Category
          <IconButton
            aria-label="close"
            onClick={() => setOpenDeleteCategoryConfirmDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {deletingCategory && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Are you sure you want to delete this category?
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mt={2} p={2} sx={{ backgroundColor: 'grey.100', borderRadius: 1 }}>
                {deletingCategory.imageUrl && (
                  <img
                    src={deletingCategory.imageUrl}
                    alt={deletingCategory.name}
                    style={{ 
                      width: '60px', 
                      height: '45px', 
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}
                  />
                )}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    {deletingCategory.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {deletingCategory.id}
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    Subcategories: {deletingCategory.poojaSubcategories?.length || 0}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="error.main" mt={2}>
                <strong>Warning:</strong> This action cannot be undone. The category and all its associated subcategories will be permanently deleted.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setOpenDeleteCategoryConfirmDialog(false)} 
            variant="outlined"
            size="large"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDeleteCategory} 
            variant="contained" 
            color="error"
            size="large"
            sx={{ fontWeight: 'bold' }}
          >
            Delete Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Subcategory Dialog */}
      <Dialog
        open={openSubCategoryDialog}
        onClose={() => setOpenSubCategoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: 'secondary.main', color: 'white' }}>
          Add Pooja Subcategory
          <IconButton
            aria-label="close"
            onClick={() => setOpenSubCategoryDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Pooja Category</InputLabel>
            <Select
              value={subCategory.poojaCategoryId}
              label="Pooja Category"
              onChange={e => setSubCategory({ ...subCategory, poojaCategoryId: e.target.value })}
            >
              {categories.map(cat => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Subcategory Name"
            margin="normal"
            value={subCategory.name}
            onChange={e => setSubCategory({ ...subCategory, name: e.target.value })}
          />

          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={1}
            value={subCategory.description}
            onChange={e => setSubCategory({ ...subCategory, description: e.target.value })}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Price"
                margin="normal"
                value={subCategory.price}
                onChange={e => setSubCategory({ ...subCategory, price: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Offer Price"
                margin="normal"
                value={subCategory.offerPrice}
                onChange={e => setSubCategory({ ...subCategory, offerPrice: e.target.value })}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            label="Image URL"
            margin="normal"
            value={subCategory.imageUrl}
            onChange={e => setSubCategory({ ...subCategory, imageUrl: e.target.value })}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Duration (e.g. 1 Hour)"
                margin="normal"
                value={subCategory.durationTime}
                onChange={e => setSubCategory({ ...subCategory, durationTime: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Remarks"
                margin="normal"
                value={subCategory.remarks}
                onChange={e => setSubCategory({ ...subCategory, remarks: e.target.value })}
              />
            </Grid>
          </Grid>

          <Button 
            variant="contained" 
            fullWidth 
            onClick={handleSubCategorySubmit} 
            sx={{ mt: 3, py: 1.5, fontWeight: 'bold' }}
            size="large"
          >
            Create Subcategory
          </Button>
        </DialogContent>
      </Dialog>

      {/* Edit Subcategory Dialog */}
      <Dialog
        open={openEditSubCategoryDialog}
        onClose={() => setOpenEditSubCategoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: 'warning.main', color: 'white' }}>
          Edit Pooja Subcategory
          <IconButton
            aria-label="close"
            onClick={() => setOpenEditSubCategoryDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {editingSubCategory && (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel>Pooja Category</InputLabel>
                <Select
                  value={editingSubCategory.poojaCategoryId}
                  label="Pooja Category"
                  onChange={e => setEditingSubCategory({ ...editingSubCategory, poojaCategoryId: e.target.value })}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Subcategory Name"
                margin="normal"
                value={editingSubCategory.name}
                onChange={e => setEditingSubCategory({ ...editingSubCategory, name: e.target.value })}
              />

              <TextField
                fullWidth
                label="Description"
                margin="normal"
                multiline
                rows={3}
                value={editingSubCategory.description}
                onChange={e => setEditingSubCategory({ ...editingSubCategory, description: e.target.value })}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Price"
                    margin="normal"
                    value={editingSubCategory.price}
                    onChange={e => setEditingSubCategory({ ...editingSubCategory, price: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Offer Price"
                    margin="normal"
                    value={editingSubCategory.offerPrice}
                    onChange={e => setEditingSubCategory({ ...editingSubCategory, offerPrice: e.target.value })}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="Image URL"
                margin="normal"
                value={editingSubCategory.imageUrl}
                onChange={e => setEditingSubCategory({ ...editingSubCategory, imageUrl: e.target.value })}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Duration (e.g. 1 Hour)"
                    margin="normal"
                    value={editingSubCategory.durationTime}
                    onChange={e => setEditingSubCategory({ ...editingSubCategory, durationTime: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Remarks"
                    margin="normal"
                    value={editingSubCategory.remarks}
                    onChange={e => setEditingSubCategory({ ...editingSubCategory, remarks: e.target.value })}
                  />
                </Grid>
              </Grid>

              <Button 
                variant="contained" 
                fullWidth 
                onClick={handleUpdateSubCategorySubmit} 
                sx={{ mt: 3, py: 1.5, fontWeight: 'bold', backgroundColor: 'warning.main' }}
                size="large"
              >
                Update Subcategory
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteConfirmDialog}
        onClose={() => setOpenDeleteConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: 'error.main', color: 'white' }}>
          Confirm Delete
          <IconButton
            aria-label="close"
            onClick={() => setOpenDeleteConfirmDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {deletingSubCategory && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Are you sure you want to delete this subcategory?
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mt={2} p={2} sx={{ backgroundColor: 'grey.100', borderRadius: 1 }}>
                {deletingSubCategory.imageUrl && (
                  <img
                    src={deletingSubCategory.imageUrl}
                    alt={deletingSubCategory.name}
                    style={{ 
                      width: '60px', 
                      height: '45px', 
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}
                  />
                )}
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    {deletingSubCategory.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {deletingSubCategory.id}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    Price: ₹{deletingSubCategory.price} | Offer: ₹{deletingSubCategory.offerPrice}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="error.main" mt={2}>
                <strong>Warning:</strong> This action cannot be undone. The subcategory will be permanently deleted.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setOpenDeleteConfirmDialog(false)} 
            variant="outlined"
            size="large"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            size="large"
            sx={{ fontWeight: 'bold' }}
          >
            Delete Subcategory
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}